//region [[ Props ]]
import * as React from "react";
import { AnswerElementExchange } from "../answers/AnswerElementExchange";
import {
  AnswerText,
  getAnswerTextByType,
} from "../answers/getAnswerTextByType";
import { GenderValue } from "shared";
import {
  ConversationNextQuestionSuggestions,
  QualifiedFeature,
  QuestionChoiceType,
} from "fe-common/graphql";
import clsx from "clsx";
import { NumericFeatureIds } from "../Questions/rangeQuestions/rangeIds";

export interface GetFormAnswerTextProps {
  translate: (KEY: string) => string;
  exchange: AnswerElementExchange;
  gender: GenderValue;
}

//endregion [[ Props ]]

//region [[ Functions ]]
const isSystolicQuestion = (q: ConversationNextQuestionSuggestions) =>
  q.suggestion.feature.id === NumericFeatureIds.SystolicBloodPressure;

// const isSystolicAnswer = (a: QualifiedFeature) =>
//   a.feature.id === NumericFeatureIds.SystolicBloodPressure;

const isDiastolicQuestion = (q: ConversationNextQuestionSuggestions) =>
  q.suggestion.feature.id === NumericFeatureIds.DiastolicBloodPressure;

const isDiastolicAnswer = (a: QualifiedFeature) =>
  a.feature.id === NumericFeatureIds.DiastolicBloodPressure;

const buildExchange = (
  exchange: AnswerElementExchange,
  q: ConversationNextQuestionSuggestions,
) => {
  return {
    ...exchange,
    type: q.questionType,
    question: {
      ...exchange.question,
      questionType: q.questionType,
      questionChoiceType:
        exchange.question.questionChoiceType ||
        QuestionChoiceType.ChooseNumeric,
      suggestions: isSystolicQuestion(q)
        ? [
            q,
            exchange.question.suggestions.find((q) => isDiastolicQuestion(q)), // Make sure systolic and diastolic questions are always paired together
          ].filter((t) => t)
        : [q],
    },
    answer: {
      ...exchange.answer,
      qfs: exchange.answer.qfs.filter(
        (qf) =>
          qf.feature.id === q.suggestion.feature.id ||
          (isDiastolicAnswer(qf) && isSystolicQuestion(q)),
      ),
    },
  };
};
//endregion [[ Functions ]]

export const getFormAnswerText = ({
  translate,
  exchange,
  gender,
  ...props
}: GetFormAnswerTextProps): AnswerText => {
  const questions = exchange?.question?.suggestions;

  const text = questions
    .filter((q) =>
      exchange.answer.qfs.some(
        (qf) =>
          qf.feature.id === q.suggestion.feature.id &&
          !isDiastolicAnswer(qf) && // Make diastolic be part of systolic
          !qf.skip &&
          !qf.negate,
      ),
    )
    ?.map((q) => {
      const dummyFormExchange = buildExchange(exchange, q);
      const answerText = getAnswerTextByType(
        translate,
        dummyFormExchange,
        gender,
        true,
      );
      return answerText;
    })
    .reduce(
      (acc, current, currentIndex, array) => {
        return {
          keepLtr: current.keepLtr || acc.keepLtr,
          isNumeric: current.isNumeric || acc.isNumeric,
          texts: [
            ...acc.texts,
            ...(currentIndex === 0
              ? [
                  <span
                    dangerouslySetInnerHTML={{ __html: current.text as string }}
                  />,
                ]
              : [
                  <br />,
                  current.multiQuestionPrefix,
                  " ",
                  <span
                    className={clsx(
                      current.keepLtr ? "ltr" : "",
                      current.isNumeric ? "numeric" : "",
                    )}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: current.text as string,
                      }}
                    />
                  </span>,
                ]),
          ],
        };
      },
      { texts: [], keepLtr: false, isNumeric: false },
    );
  return {
    text: text.texts,
    keepLtr: text.keepLtr,
    isNumeric: text.isNumeric,
    isLongFormat: true,
  };
};
